import { Controller } from "@hotwired/stimulus"

// MFA Controller for verification code input and countdown timer
//
// Targets:
//   - codeInput: The 6-digit code input field
//   - submitButton: The submit button
//   - countdown: Element displaying expiration countdown
//   - resendButton: Button to resend code
//   - resendCooldown: Element showing resend cooldown
//   - resendTimer: Element showing resend timer seconds
//
// Actions:
//   - formatInput: Format code input (digits only)
//   - handleKeyPress: Only allow numeric input
//   - resend: Resend verification code
//
export default class extends Controller {
  static targets = [
    "codeInput",
    "submitButton",
    "countdown",
    "resendButton",
    "resendCooldown",
    "resendTimer"
  ]

  connect() {
    // Auto-focus the code input
    if (this.hasCodeInputTarget) {
      this.codeInputTarget.focus()
    }

    // Start countdown timer if expires_at is set
    if (this.hasCountdownTarget) {
      this.startCountdown()
    }

    // Start resend cooldown (30 seconds after page load)
    this.startResendCooldown()
  }

  disconnect() {
    // Clean up timers
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
    }
    if (this.resendInterval) {
      clearInterval(this.resendInterval)
    }
  }

  // Format input to digits only
  formatInput(event) {
    const input = event.target
    const value = input.value

    // Remove non-digit characters
    const digitsOnly = value.replace(/\D/g, '')

    // Update input value
    input.value = digitsOnly

    // Auto-submit when 6 digits are entered
    if (digitsOnly.length === 6) {
      // Disable submit button to prevent double submission
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = true
      }

      // Submit the form after a brief delay (allows user to see full code)
      setTimeout(() => {
        this.element.requestSubmit()
      }, 300)
    }
  }

  // Only allow numeric keys (and control keys)
  handleKeyPress(event) {
    const key = event.key
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Enter']

    // Allow control keys
    if (allowedKeys.includes(key)) {
      return true
    }

    // Allow numeric keys (0-9)
    if (key >= '0' && key <= '9') {
      return true
    }

    // Block all other keys
    event.preventDefault()
    return false
  }

  // Start countdown timer for code expiration
  startCountdown() {
    const expiresAtStr = this.countdownTarget.dataset.expiresAt
    if (!expiresAtStr) return

    const expiresAt = new Date(expiresAtStr)

    // Update immediately
    this.updateCountdown(expiresAt)

    // Update every second
    this.countdownInterval = setInterval(() => {
      this.updateCountdown(expiresAt)
    }, 1000)
  }

  // Update countdown display
  updateCountdown(expiresAt) {
    const now = new Date()
    const diff = expiresAt - now

    if (diff <= 0) {
      this.countdownTarget.textContent = "00:00"
      this.countdownTarget.classList.add("text-red-400")

      // Stop the countdown
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
      }

      return
    }

    // Calculate minutes and seconds
    const totalSeconds = Math.floor(diff / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60

    // Format as MM:SS
    const formatted = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    this.countdownTarget.textContent = formatted

    // Change color when less than 2 minutes remain
    if (totalSeconds < 120) {
      this.countdownTarget.classList.add("text-orange-400")
    }
  }

  // Start resend cooldown (30 seconds)
  startResendCooldown() {
    if (!this.hasResendButtonTarget) return

    // Disable resend button
    this.resendButtonTarget.disabled = true

    // Show cooldown message
    if (this.hasResendCooldownTarget) {
      this.resendCooldownTarget.classList.remove("hidden")
    }

    let secondsRemaining = 30

    // Update timer
    const updateTimer = () => {
      if (this.hasResendTimerTarget) {
        this.resendTimerTarget.textContent = secondsRemaining
      }

      secondsRemaining--

      if (secondsRemaining < 0) {
        // Cooldown complete
        clearInterval(this.resendInterval)
        this.resendButtonTarget.disabled = false

        if (this.hasResendCooldownTarget) {
          this.resendCooldownTarget.classList.add("hidden")
        }
      }
    }

    // Update immediately
    updateTimer()

    // Update every second
    this.resendInterval = setInterval(updateTimer, 1000)
  }

  // Resend verification code
  resend(event) {
    event.preventDefault()

    // Disable button during request
    this.resendButtonTarget.disabled = true

    // Create a form and submit it
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = this.resendButtonTarget.dataset.resendUrl || '/oauth/mfa/resend'

    // Add CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    document.body.appendChild(form)
    form.submit()
  }
}
